import JSZip from 'jszip';
import { buildBoundaries } from './boundary';
import { decodePaintTree, resolvedState } from './paintCodec';
import { edgeKey, midpoint, triangleNormal } from './geometry';
import type { EditorDocument, Filament, PaintLeaf, PaintNode, SourcePart, SourceTriangle, Vec3 } from './types';

const MAX_INPUT_BYTES = 500 * 1024 * 1024;
const MAX_XML_CHARS = 500 * 1024 * 1024;
const MAX_SOURCE_TRIANGLES = 2_000_000;
const MAX_PAINT_LEAVES = 3_000_000;

const UNIT_TO_MM: Record<string, number> = {
  micron: 0.001, millimeter: 1, centimeter: 10, inch: 25.4,
  foot: 304.8, meter: 1000,
};

interface ObjectMeta {
  name: string;
  extruder: number;
  parts: { id: string; extruder: number; subtype: string }[];
}

interface ModelDoc { path: string; xml: string; doc: Document }

function parseXml(xml: string, path: string): Document {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) throw new Error(`${path} contains a prohibited XML declaration`);
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const error = doc.querySelector('parsererror');
  if (error) throw new Error(`Invalid XML in ${path}`);
  return doc;
}

function elements(parent: ParentNode, localName: string): Element[] {
  return Array.from(parent.querySelectorAll('*')).filter((node) => node.localName === localName);
}

function directChildren(parent: Element, localName: string): Element[] {
  return Array.from(parent.children).filter((node) => node.localName === localName);
}

function normalizePath(path: string): string { return path.replace(/^\//, ''); }
function numberAttr(element: Element, name: string, fallback = 0): number {
  const value = Number(element.getAttribute(name));
  return Number.isFinite(value) ? value : fallback;
}

function parseTransform(raw: string | null): number[] | null {
  if (!raw) return null;
  const result = raw.trim().split(/\s+/).map(Number);
  return result.length >= 12 && result.every(Number.isFinite) ? result : null;
}

function combineTransforms(outer: number[] | null, inner: number[] | null): number[] | null {
  if (!outer) return inner;
  if (!inner) return outer;
  const a = outer; const b = inner;
  return [
    a[0]*b[0]+a[3]*b[1]+a[6]*b[2], a[1]*b[0]+a[4]*b[1]+a[7]*b[2], a[2]*b[0]+a[5]*b[1]+a[8]*b[2],
    a[0]*b[3]+a[3]*b[4]+a[6]*b[5], a[1]*b[3]+a[4]*b[4]+a[7]*b[5], a[2]*b[3]+a[5]*b[4]+a[8]*b[5],
    a[0]*b[6]+a[3]*b[7]+a[6]*b[8], a[1]*b[6]+a[4]*b[7]+a[7]*b[8], a[2]*b[6]+a[5]*b[7]+a[8]*b[8],
    a[0]*b[9]+a[3]*b[10]+a[6]*b[11]+a[9], a[1]*b[9]+a[4]*b[10]+a[7]*b[11]+a[10], a[2]*b[9]+a[5]*b[10]+a[8]*b[11]+a[11],
  ];
}

function transformed(point: Vec3, matrix: number[] | null, unitToMm: number): Vec3 {
  if (!matrix) return [point[0] * unitToMm, point[1] * unitToMm, point[2] * unitToMm];
  const [x, y, z] = point;
  return [
    (matrix[0]*x + matrix[3]*y + matrix[6]*z + matrix[9]) * unitToMm,
    (matrix[1]*x + matrix[4]*y + matrix[7]*z + matrix[10]) * unitToMm,
    (matrix[2]*x + matrix[5]*y + matrix[8]*z + matrix[11]) * unitToMm,
  ];
}

function parseFilaments(zip: JSZip): Promise<Filament[]> {
  return (async () => {
    const file = zip.file('Metadata/project_settings.config');
    if (!file) return [{ index: 1, name: 'Filament 1', color: '#8A919A' }];
    try {
      const data = JSON.parse(await file.async('text')) as Record<string, unknown>;
      const colors = Array.isArray(data.filament_colour) ? data.filament_colour as string[] : [];
      const types = Array.isArray(data.filament_type) ? data.filament_type as string[] : [];
      if (!colors.length) return [{ index: 1, name: 'Filament 1', color: '#8A919A' }];
      return colors.map((raw, index) => ({
        index: index + 1,
        name: types[index] ? `${types[index]} · ${index + 1}` : `Filament ${index + 1}`,
        color: normalizeColor(raw),
      }));
    } catch {
      return [{ index: 1, name: 'Filament 1', color: '#8A919A' }];
    }
  })();
}

function normalizeColor(raw: string): string {
  const value = raw.startsWith('#') ? raw : `#${raw}`;
  return /^#[0-9a-f]{6}/i.test(value) ? value.slice(0, 7).toUpperCase() : '#8A919A';
}

async function parseObjectMetadata(zip: JSZip): Promise<Map<string, ObjectMeta>> {
  const result = new Map<string, ObjectMeta>();
  const file = zip.file('Metadata/model_settings.config');
  if (!file) return result;
  const xml = await file.async('text');
  const doc = parseXml(xml, 'Metadata/model_settings.config');
  for (const object of elements(doc, 'object')) {
    const id = object.getAttribute('id') || '';
    let name = `Object ${id}`;
    let extruder = 1;
    const parts: ObjectMeta['parts'] = [];
    for (const child of Array.from(object.children)) {
      if (child.localName === 'metadata') {
        const key = child.getAttribute('key'); const value = child.getAttribute('value') || '';
        if (key === 'name' && value) name = value;
        if (key === 'extruder') extruder = Math.max(1, Number.parseInt(value, 10) || 1);
      } else if (child.localName === 'part') {
        let partExtruder = 0;
        for (const metadata of elements(child, 'metadata')) {
          if (metadata.getAttribute('key') === 'extruder') partExtruder = Number.parseInt(metadata.getAttribute('value') || '0', 10) || 0;
        }
        parts.push({
          id: child.getAttribute('id') || '',
          extruder: partExtruder,
          subtype: child.getAttribute('subtype') || 'normal_part',
        });
      }
    }
    result.set(id, { name, extruder, parts });
  }
  return result;
}

function materializeNode(
  node: PaintNode,
  corners: [Vec3, Vec3, Vec3],
  path: number[],
  addLeaf: (corners: [Vec3, Vec3, Vec3], path: number[], state: number) => void,
): void {
  if (!node.children || node.splitSides === 0) {
    addLeaf(corners, path, node.state);
    return;
  }
  const k = node.specialSide % 3;
  const v0 = corners[k]; const v1 = corners[(k + 1) % 3]; const v2 = corners[(k + 2) % 3];
  const next = (child: number, points: [Vec3, Vec3, Vec3]) => materializeNode(node.children![child], points, [...path, child], addLeaf);
  if (node.splitSides === 1) {
    const m12 = midpoint(v1, v2);
    next(0, [v0, v1, m12]); next(1, [m12, v2, v0]);
  } else if (node.splitSides === 2) {
    const m01 = midpoint(v0, v1); const m20 = midpoint(v2, v0);
    next(0, [v0, m01, m20]); next(1, [m01, v1, m20]); next(2, [v1, v2, m20]);
  } else {
    const m01 = midpoint(v0, v1); const m12 = midpoint(v1, v2); const m20 = midpoint(v2, v0);
    next(0, [v0, m01, m20]); next(1, [m01, v1, m12]);
    next(2, [m12, v2, m20]); next(3, [m01, m12, m20]);
  }
}

function buildAdjacency(leaves: PaintLeaf[]): void {
  const edges = new Map<string, number[]>();
  for (const leaf of leaves) {
    const [a, b, c] = leaf.vertices;
    for (const [p, q] of [[a, b], [b, c], [c, a]] as [Vec3, Vec3][]) {
      const key = edgeKey(p, q);
      const list = edges.get(key);
      if (list) list.push(leaf.id); else edges.set(key, [leaf.id]);
    }
  }
  for (const ids of edges.values()) {
    if (ids.length < 2) continue;
    for (const a of ids) for (const b of ids) if (a !== b && !leaves[a].neighbors.includes(b)) leaves[a].neighbors.push(b);
  }
}

function extractPart(
  model: ModelDoc,
  objectId: number,
  partIndex: number,
  name: string,
  defaultExtruder: number,
  matrix: number[] | null,
  unitToMm: number,
): SourcePart | null {
  const object = elements(model.doc, 'object').find((candidate) => numberAttr(candidate, 'id', -1) === objectId);
  if (!object || (object.getAttribute('type') || 'model') !== 'model') return null;
  const mesh = directChildren(object, 'mesh')[0] || elements(object, 'mesh')[0];
  if (!mesh) return null;
  const vertexElements = elements(mesh, 'vertex');
  const vertices = vertexElements.map((vertex): Vec3 => transformed([
    numberAttr(vertex, 'x'), numberAttr(vertex, 'y'), numberAttr(vertex, 'z'),
  ], matrix, unitToMm));
  const allModelTriangles = elements(model.doc, 'triangle');
  const triangleElements = elements(mesh, 'triangle');
  const triangles: SourceTriangle[] = [];
  const leaves: PaintLeaf[] = [];
  for (let triangleIndex = 0; triangleIndex < triangleElements.length; triangleIndex += 1) {
    const element = triangleElements[triangleIndex];
    const indices = [numberAttr(element, 'v1'), numberAttr(element, 'v2'), numberAttr(element, 'v3')] as [number, number, number];
    const corners = indices.map((index) => vertices[index]) as [Vec3, Vec3, Vec3];
    if (corners.some((corner) => !corner)) continue;
    const originalPaint = element.getAttribute('paint_color') || '';
    const tree = decodePaintTree(originalPaint);
    const sourceIndex = triangles.length;
    triangles.push({
      vertices: indices,
      normal: triangleNormal(...corners),
      originalPaint,
      tree,
      modelTriangleIndex: allModelTriangles.indexOf(element),
    });
    materializeNode(tree, corners, [], (leafCorners, path, state) => {
      if (leaves.length >= MAX_PAINT_LEAVES) throw new Error(`Paint leaf limit (${MAX_PAINT_LEAVES.toLocaleString()}) exceeded`);
      leaves.push({
        id: leaves.length,
        partIndex,
        sourceTriangle: sourceIndex,
        path,
        vertices: leafCorners,
        normal: triangleNormal(...leafCorners),
        extruder: resolvedState(state, defaultExtruder),
        neighbors: [],
      });
    });
  }
  buildAdjacency(leaves);
  return { key: `${model.path}#${objectId}`, name, objectId, modelPath: model.path, defaultExtruder, vertices, triangles, leaves };
}

export async function parse3MF(bytes: Uint8Array, fileName: string): Promise<EditorDocument> {
  if (bytes.byteLength > MAX_INPUT_BYTES) throw new Error('3MF exceeds the 500 MB input limit');
  const zip = await JSZip.loadAsync(bytes, { checkCRC32: false });
  const modelDocs = new Map<string, ModelDoc>();
  for (const [rawPath, entry] of Object.entries(zip.files)) {
    if (entry.dir || !rawPath.toLowerCase().endsWith('.model')) continue;
    const path = normalizePath(rawPath);
    const xml = await entry.async('text');
    if (xml.length > MAX_XML_CHARS) throw new Error(`${path} exceeds the XML entry limit`);
    modelDocs.set(path, { path, xml, doc: parseXml(xml, path) });
  }
  const root = modelDocs.get('3D/3dmodel.model');
  if (!root) throw new Error('No 3D/3dmodel.model entry was found');
  const unit = (root.doc.documentElement.getAttribute('unit') || 'millimeter').toLowerCase();
  const unitToMillimeters = UNIT_TO_MM[unit] ?? 1;
  const filaments = await parseFilaments(zip);
  const metadata = await parseObjectMetadata(zip);
  const buildTransforms = new Map<number, number[] | null>();
  for (const item of elements(root.doc, 'item')) buildTransforms.set(numberAttr(item, 'objectid'), parseTransform(item.getAttribute('transform')));

  const warnings: string[] = [];
  const parts: SourcePart[] = [];
  let sourceTriangleCount = 0;
  const rootObjects = elements(root.doc, 'object');
  for (const rootObject of rootObjects) {
    if ((rootObject.getAttribute('type') || 'model') !== 'model') continue;
    const rootId = numberAttr(rootObject, 'id');
    const meta = metadata.get(String(rootId));
    const objectName = meta?.name || `Object ${rootId}`;
    const objectExtruder = meta?.extruder || 1;
    const buildTransform = buildTransforms.get(rootId) ?? null;
    const componentsContainer = directChildren(rootObject, 'components')[0];
    const components = componentsContainer ? directChildren(componentsContainer, 'component') : [];
    if (!components.length) {
      const part = extractPart(root, rootId, parts.length, objectName, objectExtruder, buildTransform, unitToMillimeters);
      if (part) { parts.push(part); sourceTriangleCount += part.triangles.length; }
      continue;
    }
    for (let index = 0; index < components.length; index += 1) {
      const component = components[index];
      const componentId = numberAttr(component, 'objectid');
      const partMeta = meta?.parts.length === components.length
        ? meta.parts[index]
        : meta?.parts.find((candidate) => Number(candidate.id) === componentId);
      if (partMeta?.subtype === 'negative_part' || partMeta?.subtype === 'modifier_part') continue;
      const pathAttribute = Array.from(component.attributes).find((attribute) => attribute.localName === 'path')?.value || '';
      const model = pathAttribute ? modelDocs.get(normalizePath(pathAttribute)) : root;
      if (!model) { warnings.push(`Missing component model: ${pathAttribute}`); continue; }
      const defaultExtruder = partMeta?.extruder && partMeta.extruder > 0 ? partMeta.extruder : objectExtruder;
      const matrix = combineTransforms(buildTransform, parseTransform(component.getAttribute('transform')));
      const part = extractPart(model, componentId, parts.length, `${objectName} · Part ${index + 1}`, defaultExtruder, matrix, unitToMillimeters);
      if (part) { parts.push(part); sourceTriangleCount += part.triangles.length; }
    }
  }
  if (sourceTriangleCount > MAX_SOURCE_TRIANGLES) throw new Error(`Source triangle limit (${MAX_SOURCE_TRIANGLES.toLocaleString()}) exceeded`);
  if (!parts.length) throw new Error('No printable mesh geometry was found');
  const duplicateKeys = parts.filter((part, index) => parts.findIndex((candidate) => candidate.key === part.key) !== index);
  if (duplicateKeys.length) warnings.push('Instanced mesh definitions are shared; paint edits are synchronized across every reference.');
  const { chains, groups, segments } = buildBoundaries(parts);
  return {
    fileName, originalBytes: bytes, unit, unitToMillimeters, filaments, parts,
    boundaries: chains, boundaryGroups: groups, boundarySegments: segments, dirty: false, warnings,
  };
}

export function rebuildPartLeaves(part: SourcePart, partIndex: number): void {
  const leaves: PaintLeaf[] = [];
  for (let sourceIndex = 0; sourceIndex < part.triangles.length; sourceIndex += 1) {
    const triangle = part.triangles[sourceIndex];
    const corners = triangle.vertices.map((index) => part.vertices[index]) as [Vec3, Vec3, Vec3];
    materializeNode(triangle.tree, corners, [], (leafCorners, path, state) => {
      leaves.push({ id: leaves.length, partIndex, sourceTriangle: sourceIndex, path, vertices: leafCorners,
        normal: triangleNormal(...leafCorners), extruder: resolvedState(state, part.defaultExtruder), neighbors: [] });
    });
  }
  buildAdjacency(leaves);
  part.leaves = leaves;
}
